import { M365Alert, WithStatusWrapper } from "Shared/Components";
import {
    M365PulseForm,
    M365PulseFormComboboxField,
    M365PulseFormInputField,
    M365PulseFormSubmitButton,
    M365PulseFormTextareaField
} from "Shared/Components/Form";
import React, { useCallback, useMemo, useState } from "react";
import { Types, newGuid } from "Shared/Utils";

import { Button } from "@fluentui/react-components";
import { FeedbackCategoryOption } from "./FeedbackCategoryOption";
import { FeedbackCategory, IUserFeedbackFlyoutProps } from "./UserFeedback.types";
import { feedbackCategoryItems, feedbackDeviceLabel } from "./UserFeedback.constants";
import { observer } from "mobx-react";
import { toRenderingStatus } from "../../Enumerations/RenderingStatus";
import { useAppSettings, useSmallScreen } from "Shared/Hooks";
import { useFeedbackStyle } from "./UserFeedback.styles";
import { useRootStore } from "Store/RootStore";
import { z } from "zod";
import { useORPCQuery, UserFeedbackModel } from "Core/oRPC";
import { useMutation } from "@tanstack/react-query";

const formSchema = z.object({
    category: z
        .array(z.enum([FeedbackCategory.ReportProblem, FeedbackCategory.GiveCompliment, FeedbackCategory.SuggestFeature]))
        .min(1, "category is required"),
    comment: z.string().min(1, "comment is required").max(5000, "The maximum number of characters exceeded"),
    url: z.string().min(1, "url is required")
});
type TFormSchema = z.infer<typeof formSchema>;

const UserFeedbackBase = (props: IUserFeedbackFlyoutProps) => {
    const classes = useFeedbackStyle();
    const { onSubmit, onSubmitData, onDismiss, selectedApp } = props;
    const { currentApp } = useRootStore("AppSettingStore");
    const { m365appVersion } = useAppSettings();
    const isSmallScreen = useSmallScreen();
    const deviceType = isSmallScreen ? feedbackDeviceLabel.Mobile : feedbackDeviceLabel.Desktop;
    const { coreApi } = useORPCQuery();
    const { mutate, status } = useMutation(coreApi.postUserFeedback.mutationOptions());

    const [statusMessage, setStatusMessage] = useState(Types.getUndefined<string>());
    const targetApp = selectedApp ?? currentApp;

    const appOwnerRecipients = useMemo(
        () => (targetApp.team.toLocaleLowerCase() === "osp" ? [] : targetApp.owner.split(";")),
        [targetApp.owner, targetApp.team]
    );

    const commentTextArea = useMemo(
        () => ({
            className: classes.commentTextArea
        }),
        [classes.commentTextArea]
    );

    const initialValues: TFormSchema = useMemo(
        () => ({
            category: [],
            url: window.location.href,
            comment: ""
        }),
        []
    );

    const submitFeedback = useCallback(
        (values: TFormSchema) => {
            const requestId = newGuid();
            const category = values.category[0];
            if (!category) {
                setStatusMessage("Select a feedback category.");
                return;
            }

            const model: UserFeedbackModel = {
                Url: values.url,
                Message: `Feedback category: ${category}\nDevice: ${deviceType}\n\n${values.comment}`,
                FeedbackCategory: category,
                AppName: targetApp.title,
                TeamName: targetApp.team,
                FrontendVersion: m365appVersion ?? "N/A",
                CcRecipients: appOwnerRecipients,
                RequestId: requestId
            };
            mutate(model, {
                onSuccess: () => {
                    setStatusMessage("Your feedback has been submitted.");
                    onSubmit?.();
                    onSubmitData?.({ requestId, comment: values.comment });
                },
                onError: () => setStatusMessage("Failed to submit the feedback. Please try again.")
            });
        },
        [appOwnerRecipients, targetApp.team, targetApp.title, m365appVersion, deviceType, mutate, onSubmit, onSubmitData]
    );

    return (
        <>
            <M365PulseForm className={classes.root} initialValues={initialValues} onSubmit={submitFeedback} schema={formSchema}>
                <M365PulseFormInputField required name="url" aria-label="url" label="Location" className={classes.inputField} />
                <M365PulseFormComboboxField required name="category" label="Select a category" placeholder="Select a category" className={classes.inputField}>
                    {feedbackCategoryItems.map((item) => (
                        <FeedbackCategoryOption key={item.category} {...item} />
                    ))}
                </M365PulseFormComboboxField>
                <M365PulseFormTextareaField required name="comment" label="Comment" textarea={commentTextArea} className={classes.inputField} />
                <div className={classes.buttons}>
                    <M365PulseFormSubmitButton appearance="primary" className={classes.submitButton} disabled={status === "pending" || status === "success"}>
                        Submit
                    </M365PulseFormSubmitButton>
                    <Button onClick={onDismiss} className={classes.cancelButton}>
                        Cancel
                    </Button>
                </div>
            </M365PulseForm>
            <WithStatusWrapper loadingStatus={status !== "idle" ? toRenderingStatus[status] : undefined} errorMessage={statusMessage}>
                {status === "success" ? <M365Alert intent="success" message={statusMessage} className={classes.root} /> : null}
            </WithStatusWrapper>
        </>
    );
};

export const UserFeedback = observer(UserFeedbackBase);
